import React from "react";
import { useTranslation } from "react-i18next";
import { HelpCircle, Gift, History, Smartphone, Sparkles } from "lucide-react";
import useShakeVisualMotion from "../hooks/useShakeVisualMotion";

const asset = (name) => `${process.env.PUBLIC_URL}/images/game/${name}.webp`;

export default function ShakeHome({
  authLoading,
  programData,
  upcomingStartsIn,
  sessionActive,
  activityEnded,
  balance,
  balanceLoaded,
  permissionGranted,
  permissionRequested,
  motionUnavailable,
  isShaking,
  boxShakeAnimation,
  rewardResult,
  currentUserReward,
  manualShakeButtonText,
  shouldDisableShakeButton,
  onAllow,
  onManualShake,
  onRewards,
  onEntries,
  onHelp,
  rewardPresentation,
}) {
  const { t } = useTranslation();
  const artRef = useShakeVisualMotion();
  const noWin =
    !isShaking &&
    !boxShakeAnimation &&
    rewardResult?.success &&
    rewardResult?.data?.consumed &&
    !rewardResult?.data?.reward;
  const description =
    isShaking || boxShakeAnimation
      ? t("main.shakeButton.processing")
      : upcomingStartsIn && !programData
        ? t(`main.startsIn.${upcomingStartsIn.unit}`, {
            count: upcomingStartsIn.count,
          })
        : !sessionActive && !programData
          ? t(activityEnded ? "sessionEnded.message" : "game.noEvent")
          : !balanceLoaded
            ? t("main.loadingShakeData")
            : balance > 0
              ? null
              : t("main.allUsed");
  return (
    <div className="shake-home">
      <div className="shake-home-top">
        {upcomingStartsIn && !programData ? (
          <span className="shake-time-chip">
            {t("upcoming.title")} ·{" "}
            {t(`main.startsIn.${upcomingStartsIn.unit}`, {
              count: upcomingStartsIn.count,
            })}
          </span>
        ) : (
          <span className="shake-time-chip">
            <Sparkles size={15} aria-hidden="true" /> {t("game.rewardTime")}
          </span>
        )}
        <button
          className="shake-help"
          type="button"
          onClick={onHelp}
          aria-label={t("game.help")}
        >
          <HelpCircle size={22} aria-hidden="true" />
        </button>
      </div>
      {authLoading ? (
        <div
          className="shake-skeleton"
          role="status"
          aria-label={t("game.loading")}
        >
          <div className="ui-skeleton shake-skeleton-art" />
          <div className="ui-skeleton shake-skeleton-line" />
          <button
            type="button"
            className="shake-game-button"
            aria-disabled="true"
            onClick={() => onManualShake("shake-now-button")}
          >
            {t("main.shakeButton.preparing")}
          </button>
        </div>
      ) : (
        <>
          <div
            className={`gift-section shake-stage ${isShaking || boxShakeAnimation ? "is-shaking" : ""}`}
          >
            <div className="shake-stage-glow" />
            <img
              ref={artRef}
              className="shake-tree"
              src={asset("money-tree")}
              alt=""
            />
            <img className="shake-phone" src={asset("phone-shake")} alt="" />
            {noWin && <img className="shake-leaf" src={asset("leaf")} alt="" />}
          </div>
          <div className="shake-copy">
            <h1>
              {!sessionActive && upcomingStartsIn
                ? t("upcoming.title")
                : !sessionActive
                  ? t(
                      activityEnded
                        ? "sessionEnded.title"
                        : "game.noEventTitle",
                    )
                  : noWin
                    ? t("game.keepTrying")
                    : t("main.shakeTitle")}
            </h1>
            {description && <p>{description}</p>}
            {currentUserReward && (
              <p className="shake-won">
                <Sparkles size={16} aria-hidden="true" /> {t("main.wonPrefix")}
                {t("main.wonName", { name: currentUserReward.name })}
              </p>
            )}
          </div>
          <div className="shake-balance" aria-live="polite" aria-atomic="true">
            <span>{t("game.balance")}</span>
            {balanceLoaded ? (
              <strong>{balance}</strong>
            ) : (
              <span className="ui-skeleton shake-balance-skeleton" />
            )}
          </div>
          <div className="shake-primary">
            {motionUnavailable ? (
              <button
                type="button"
                className="shake-game-button"
                aria-disabled={shouldDisableShakeButton}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!shouldDisableShakeButton)
                    onManualShake("shake-now-button");
                }}
              >
                {!sessionActive ||
                !balanceLoaded ||
                balance <= 0 ||
                shouldDisableShakeButton
                  ? manualShakeButtonText
                  : t("game.tapFallback")}
              </button>
            ) : (
              <button
                type="button"
                className="shake-game-button"
                onClick={onAllow}
              >
                <Smartphone size={20} aria-hidden="true" />{" "}
                {permissionGranted
                  ? t("main.reenablePhoneShake")
                  : permissionRequested
                    ? t("main.reenablePhoneShake")
                    : t("game.startShake")}
              </button>
            )}
            {!motionUnavailable && !permissionGranted && (
              <p className="shake-permission-hint">{t("game.motionHint")}</p>
            )}
          </div>
          <div className="shake-actions">
            <button type="button" onClick={onRewards}>
              <Gift size={19} aria-hidden="true" />
              {t("main.myRewards")}
            </button>
            <button type="button" onClick={onEntries}>
              <History size={19} aria-hidden="true" />
              {t("main.myEntries")}
            </button>
          </div>
        </>
      )}
      {rewardPresentation}
    </div>
  );
}
